<script setup>
import BrandLoader from '@/components/global/BrandLoader.vue';
/**
 * NotificationDropdown - Dropdown container for notifications
 * Shows list of notifications with mark all as read functionality
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useNotificationsStore } from '@/stores/notificationsStore';
import { useUserAuthStore } from '@/stores/userAuthStore';
import { useAdminDeliverablesStore } from '@/stores/adminDeliverablesStore';
import { notificationService } from '@/services/notificationService';
import { deliverableService } from '@/services/deliverableService';
import { projectService } from '@/services/projectService';
import { companyService } from '@/services/companyService';
import NotificationItem from './NotificationItem.vue';
import { logger } from '@/services/loggerService';

const router = useRouter();
const notificationsStore = useNotificationsStore();
const userAuthStore = useUserAuthStore();
const adminDeliverablesStore = useAdminDeliverablesStore();

// Toggle to show/hide read notifications
const showReadNotifications = ref(false);

// Use filteredItems (respects company filter for clients)
const allNotifications = computed(() => notificationsStore.filteredItems);
const unreadCount = computed(() => notificationsStore.unreadCount);
const totalCount = computed(() => notificationsStore.filteredItems.length);
const hasNotifications = computed(() => notificationsStore.hasNotifications);
const loading = computed(() => notificationsStore.loading);

// Check if user is admin (they see all notifications without company filter)
const isAdmin = computed(() => userAuthStore.isAdmin);

/**
 * companyId -> name, from the user's own memberships.
 *
 * Entries are stored as `{ companyId, companyName }` (see the normalisation in
 * userAuthStore) — reading `.id`/`.name` left this map empty for every
 * non-admin, which both hid the badge and made `hasCompanyAccess` false, so
 * their notifications rendered as inaccessible and clicking did nothing.
 * A legacy entry can carry a blank name, so the KEY is what grants access.
 */
const companyNameMap = computed(() => {
  const map = {};
  for (const entry of userAuthStore.user?.companies || []) {
    const id = typeof entry === 'string' ? entry : entry?.companyId || entry?.id;
    if (!id) continue;
    map[id] = (typeof entry === 'string' ? '' : entry?.companyName || entry?.name) || '';
  }
  return map;
});

// Get company name for a notification
const getCompanyName = (notification) => {
  if (!notification.companyId) return null;
  return companyNameMap.value[notification.companyId] || null;
};

/**
 * Whose notification is this? Anyone whose notifications span more than one
 * company needs the answer — admins, agency staff, and multi-company clients
 * alike. For someone with a single company it would be noise on every row.
 */
const shouldShowCompanyBadge = computed(
  () => isAdmin.value || Object.keys(companyNameMap.value).length > 1,
);

// Check if user has access to a company
const hasCompanyAccess = (notification) => {
  // Admins have access to everything
  if (isAdmin.value) return true;

  // No company ID means it's a system notification, always accessible
  if (!notification.companyId) return true;

  // Membership is what grants access — a blank stored name must not deny it.
  return notification.companyId in companyNameMap.value;
};

// Filter notifications based on toggle
const notifications = computed(() => {
  if (showReadNotifications.value) {
    return allNotifications.value;
  }
  return allNotifications.value.filter(n => !n.isRead);
});

const displayedCount = computed(() => notifications.value.length);

const handleNotificationClick = async (notification) => {
  logger.info('[NotificationDropdown] Notification clicked', {
    id: notification.id,
    type: notification.type,
    isRead: notification.isRead
  });

  // Mark as read if not already
  if (!notification.isRead) {
    try {
      await notificationsStore.markAsRead(notification.id);
    } catch (error) {
      logger.error('[NotificationDropdown] Failed to mark as read', error);
    }
  }

  // Handle deliverable notifications - open modal instead of navigating
  if (notificationService.shouldOpenModal(notification)) {
    try {
      // Fetch the deliverable data
      const deliverable = await deliverableService.getDeliverable(notification.entityId);
      if (!deliverable) {
        logger.warn('[NotificationDropdown] Deliverable not found', { entityId: notification.entityId });
        return;
      }

      // Fetch project and company data (required by DeliverableModal)
      let project = null;
      let company = null;

      if (deliverable.projectId) {
        project = await projectService.getProject(deliverable.projectId);
      }

      if (deliverable.companyId) {
        company = await companyService.getCompany(deliverable.companyId);
      }

      // Open the deliverable modal with all required data
      adminDeliverablesStore.openDeliverableModal(deliverable, project, company);
    } catch (error) {
      logger.error('[NotificationDropdown] Failed to open deliverable modal', error);
    }
    return;
  }

  // Navigate to the notification target
  const url = notificationService.getNotificationUrl(
    notification,
    userAuthStore.user.type,
    userAuthStore.user.agencyId || null,
  );
  if (url && url !== '/') {
    router.push(url);
  }
};

const handleMarkAllAsRead = async () => {
  logger.info('[NotificationDropdown] Marking all as read');
  try {
    await notificationsStore.markAllAsRead();
  } catch (error) {
    logger.error('[NotificationDropdown] Failed to mark all as read', error);
  }
};

const handleToggleRead = async (notification) => {
  logger.info('[NotificationDropdown] Toggling read status', {
    id: notification.id,
    currentlyRead: notification.isRead
  });
  try {
    if (notification.isRead) {
      await notificationsStore.markAsUnread(notification.id);
    } else {
      await notificationsStore.markAsRead(notification.id);
    }
  } catch (error) {
    logger.error('[NotificationDropdown] Failed to toggle read status', error);
  }
};

const handleDelete = async (notification) => {
  logger.info('[NotificationDropdown] Deleting notification', { id: notification.id });
  try {
    await notificationsStore.deleteNotification(notification.id);
  } catch (error) {
    logger.error('[NotificationDropdown] Failed to delete notification', error);
  }
};

const handleClearAllRead = async () => {
  logger.info('[NotificationDropdown] Clearing all read notifications');
  try {
    await notificationsStore.deleteAllRead();
  } catch (error) {
    logger.error('[NotificationDropdown] Failed to clear all read', error);
  }
};

// Count of read notifications for showing clear all button
const readCount = computed(() => allNotifications.value.filter(n => n.isRead).length);
</script>

<template>
  <div class="notification-dropdown">
    <!-- Header -->
    <div class="dropdown-header">
      <span class="header-title">Notifications</span>
      <!-- Segmented toggle for Unread/All -->
      <div class="view-toggle">
        <button
          class="toggle-btn"
          :class="{ 'is-active': !showReadNotifications }"
          @click.stop="showReadNotifications = false"
        >
          Unread
          <span v-if="unreadCount > 0" class="count-badge">{{ unreadCount }}</span>
        </button>
        <button
          class="toggle-btn"
          :class="{ 'is-active': showReadNotifications }"
          @click.stop="showReadNotifications = true"
        >
          All
          <span v-if="totalCount > 0" class="count-badge is-muted">{{ totalCount }}</span>
        </button>
      </div>
    </div>

    <!-- Action bar -->
    <div v-if="(!showReadNotifications && unreadCount > 0) || (showReadNotifications && readCount > 0)" class="action-bar">
      <!-- Mark all as read (when viewing unread) -->
      <button
        v-if="!showReadNotifications && unreadCount > 0"
        class="action-bar-btn"
        @click.stop="handleMarkAllAsRead"
      >
        <font-awesome-icon :icon="['fad', 'check-double']" class="mr-1" />
        Mark all as read
      </button>
      <!-- Clear all read (when viewing all) -->
      <button
        v-if="showReadNotifications && readCount > 0"
        class="action-bar-btn is-danger"
        @click.stop="handleClearAllRead"
      >
        <font-awesome-icon :icon="['fad', 'trash']" class="mr-1" />
        Clear all read
      </button>
    </div>

    <!-- Content -->
    <div class="dropdown-content-scroll">
      <!-- Loading state -->
      <div v-if="loading" class="empty-state">
        <BrandLoader />
        <p>Loading notifications...</p>
      </div>

      <!-- Empty state - no notifications at all -->
      <div v-else-if="!hasNotifications" class="empty-state">
        <font-awesome-icon :icon="['fal', 'bell']" class="empty-icon mb-3" />
        <p class="empty-title">No notifications</p>
        <p class="empty-subtitle">You're all caught up!</p>
      </div>

      <!-- Empty state - all read and toggle is off -->
      <div v-else-if="displayedCount === 0 && !showReadNotifications" class="empty-state">
        <font-awesome-icon :icon="['fal', 'check-circle']" class="empty-icon mb-3" />
        <p class="empty-title">All caught up!</p>
        <p class="empty-subtitle">No unread notifications</p>
      </div>

      <!-- Notifications list -->
      <div v-else class="notifications-list">
        <NotificationItem
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          :show-company-badge="shouldShowCompanyBadge"
          :company-name="getCompanyName(notification)"
          :has-company-access="hasCompanyAccess(notification)"
          @click="handleNotificationClick"
          @toggle-read="handleToggleRead"
          @delete="handleDelete"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.notification-dropdown {
  width: 360px;
  max-width: calc(100vw - 2rem);
  background: var(--bulma-scheme-main);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1rem;
  border-bottom: 1px solid var(--bulma-border-weak);
  background: var(--bulma-scheme-main-bis);
}

.header-title {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--bulma-text-strong);
}

// Segmented toggle styles
.view-toggle {
  display: flex;
  background: var(--bulma-scheme-main-ter);
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--bulma-text-weak);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(.is-active) {
    color: var(--bulma-text);
  }

  &.is-active {
    background: var(--bulma-primary);
    color: var(--bulma-white);

    .count-badge {
      background: rgba(255, 255, 255, 0.25);
      color: var(--bulma-white);
    }
  }
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: var(--bulma-danger);
  color: var(--bulma-white);

  &.is-muted {
    background: var(--bulma-scheme-main);
    color: var(--bulma-text-weak);
  }
}

// Action bar
.action-bar {
  display: flex;
  justify-content: flex-end;
  padding: 0.375rem 0.75rem;
  background: var(--bulma-scheme-main-bis);
  border-bottom: 1px solid var(--bulma-border-weak);
}

.action-bar-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 0.75rem;
  color: var(--bulma-primary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.1);
  }

  &.is-danger {
    color: var(--bulma-danger);

    &:hover {
      background: hsla(var(--bulma-danger-h), var(--bulma-danger-s), var(--bulma-danger-l), 0.1);
    }
  }
}

.dropdown-content-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
  color: var(--bulma-text-weak);
}

.empty-icon {
  font-size: 2.5rem;
  opacity: 0.5;
}

.empty-title {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--bulma-text);
  margin-bottom: 0.25rem;
}

.empty-subtitle {
  font-size: 0.8125rem;
}

.notifications-list {
  // Container for notification items
}
</style>
